import { useEffect, useId, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Button, Checkbox, Progress, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconCamera,
  IconCar,
  IconChevronDown,
  IconEye,
  IconFileCertificate,
  IconId,
  IconLicense,
  IconRefresh,
  IconShieldCheck,
  IconShieldLock,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import { RIDES_UPLOAD_STATUS, RIDES_UPLOAD_TYPE, type RidesUploadTypeView, type RidesUploadView } from '@actschurch/ccms-sdk';
import { DOCUMENT_STAGE_TEXT, isLiveUpload, useDocumentActions } from '@/lib/documents';
import { isTouchDevice } from '@/lib/photo';
import { rejectedCopy, type PillKind } from '@/lib/copy';
import { formatShortDate, parseApiDate, stampToDate, toYmd } from '@/lib/format';
import { useCopy } from '@/lib/settings';
import { Callout, ExpiryDateInput } from '@/components/ui';
import { Pill } from './Pill';
import '@/styles/member.css';

const ICONS: Record<number, typeof IconId> = {
  [RIDES_UPLOAD_TYPE.LicenceFront]: IconLicense,
  [RIDES_UPLOAD_TYPE.LicenceBack]: IconLicense,
  [RIDES_UPLOAD_TYPE.IdentityDocument]: IconId,
  [RIDES_UPLOAD_TYPE.VehicleLicenceDisc]: IconCar,
  [RIDES_UPLOAD_TYPE.VehicleInsurance]: IconShieldCheck,
  [RIDES_UPLOAD_TYPE.ChildSafetyClearance]: IconShieldLock,
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "30 June 2029" (no weekday for expiry dates). */
function longDate(value: string | null | undefined): string {
  const d = parseApiDate(value);
  return d ? `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : '';
}

function tomorrow(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  return d;
}

/** Status words for one document, the same everywhere (Account, driver steps, booking). */
export function documentPill(
  upload: Pick<RidesUploadView, 'statusID' | 'isExpired'> | null | undefined,
  opts: { approvalRequired?: boolean; required?: boolean } = {},
): { label: string; kind: PillKind | 'neutral' } {
  if (upload && (upload.isExpired || upload.statusID === RIDES_UPLOAD_STATUS.Expired)) return { label: 'Expired', kind: 'bad' };
  switch (upload?.statusID) {
    case RIDES_UPLOAD_STATUS.Accepted:
      return { label: 'Accepted', kind: 'ok' };
    case RIDES_UPLOAD_STATUS.Submitted:
      return opts.approvalRequired ? { label: 'Waiting for a check', kind: 'warn' } : { label: 'Uploaded', kind: 'ok' };
    case RIDES_UPLOAD_STATUS.Rejected:
      return { label: 'Not accepted', kind: 'bad' };
    default:
      return opts.required ? { label: 'Needed', kind: 'warn' } : { label: 'Not added', kind: 'neutral' };
  }
}

export interface DocumentItemProps {
  type: RidesUploadTypeView;
  upload: RidesUploadView | null;
  level: 'optional' | 'required';
  /** Sub line for a required type, e.g. "Needed before you can offer a lift". */
  requiredText: string;
  approvalRequired: boolean;
  maxMb: number;
  /** Missing and the API just asked for it (409 documents_required). */
  highlight?: boolean;
  /** Fetch + show a fresh view link. */
  onView: (upload: RidesUploadView) => void;
  viewing: boolean;
  onChanged?: () => void;
}

/**
 * One document: a 64 px row (icon, name, status pill) that opens to show what to do. Required
 * documents that still need an upload start open; everything else starts closed. Inside: help
 * text, the reason a document wasn't accepted, expiry date and consent when the type asks for
 * them, Upload / Take a photo / View / Replace / Delete, progress and plain errors.
 */
export function DocumentItem({ type, upload, level, requiredText, approvalRequired, maxMb, highlight, onView, viewing, onChanged }: DocumentItemProps) {
  const { t } = useCopy();
  const actions = useDocumentActions();
  const galleryRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const touch = useMemo(isTouchDevice, []);
  const ids = useId();
  const live = isLiveUpload(upload);
  const [open, setOpen] = useState(() => !!highlight || (level === 'required' && !live));
  const [replacing, setReplacing] = useState(false);
  const [expiry, setExpiry] = useState<Date | null>(null);
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (highlight && !live) setOpen(true);
  }, [highlight, live]);

  useEffect(() => {
    if (!status) return;
    const timer = window.setTimeout(() => setStatus(null), 6000);
    return () => window.clearTimeout(timer);
  }, [status]);

  const Icon = ICONS[type.uploadTypeID] ?? (type.isChildSafetyClearance ? IconShieldLock : IconFileCertificate);
  const rejected = upload?.statusID === RIDES_UPLOAD_STATUS.Rejected;
  const expired = !!upload && (upload.isExpired || upload.statusID === RIDES_UPLOAD_STATUS.Expired);
  const pill = documentPill(upload, { approvalRequired, required: level === 'required' });
  const showUploader = !live || replacing;
  const { busy } = actions;

  const expiryOk = !type.askExpiryDate || (!!expiry && expiry.getTime() >= tomorrow().getTime());
  const consentOk = !type.isChildSafetyClearance || consent;
  const canPick = !busy && expiryOk && consentOk;
  const accept = type.allowPdf ? 'image/*,application/pdf,.pdf' : 'image/*';
  const uploadedOn = upload?.uploadedAt ? stampToDate(upload.uploadedAt) : null;

  const sub = live
    ? `${uploadedOn ? `Uploaded ${formatShortDate(uploadedOn)}` : 'Uploaded'}${upload.expiresAt ? `. Valid until ${longDate(upload.expiresAt)}` : ''}`
    : level === 'required'
      ? requiredText
      : type.isChildSafetyClearance
        ? 'Optional. Lets you drive under-18s.'
        : 'Optional';

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    e.currentTarget.value = ''; // allow the same file again
    if (!file) return;
    setStatus(null);
    const saved = await actions.upload(type.uploadTypeID, file, {
      allowPdf: type.allowPdf,
      maxMb,
      expiresAt: type.askExpiryDate && expiry ? toYmd(expiry) : null,
      consentGranted: type.isChildSafetyClearance ? consent : null,
    });
    if (saved) {
      setReplacing(false);
      setConsent(false);
      setExpiry(null);
      setStatus(
        saved.statusID === RIDES_UPLOAD_STATUS.Accepted
          ? t('documents.accepted')
          : approvalRequired
            ? t('documents.uploaded_review')
            : t('documents.uploaded'),
      );
      onChanged?.();
    }
  };

  const confirmDelete = () => {
    if (!upload) return;
    modals.openConfirmModal({
      title: `Delete your ${type.name.toLowerCase()}?`,
      centered: true,
      children: (
        <Text size="md">
          We delete the file from Carpool straight away.
          {level === 'required' ? " You'll need to upload it again." : ''}
        </Text>
      ),
      labels: { confirm: 'Delete it', cancel: 'Keep it' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setStatus(null);
        const ok = await actions.remove(upload.rideUploadID);
        if (ok) {
          setStatus(t('documents.deleted'));
          onChanged?.();
        }
      },
    });
  };

  return (
    <div className="rm-doc" data-highlight={highlight && !live ? 'true' : undefined} role="group" aria-labelledby={`${ids}-name`}>
      <button type="button" className="head" aria-expanded={open} aria-controls={`${ids}-body`} onClick={() => setOpen((o) => !o)}>
        <span className="ic" aria-hidden="true"><Icon size={22} /></span>
        <span className="meta">
          <b id={`${ids}-name`}>{type.name}</b>
          <span className="sub">{sub}</span>
          <Pill kind={pill.kind}>{pill.label}</Pill>
        </span>
        <span className="chev" aria-hidden="true"><IconChevronDown size={22} /></span>
      </button>

      <div className="body" id={`${ids}-body`} hidden={!open}>
        {open && (
          <>
            {rejected && <Callout tone="bad">{rejectedCopy(t('docs.rejected'), upload?.reviewNotes)}</Callout>}
            {expired && !rejected && <Callout tone="warn">{t('documents.expired')}</Callout>}
            {live && !upload.hasFile && <p className="rm-small">{t('documents.purged')}</p>}

            {live && !replacing && (
              <>
                <div className="rm-btnrow">
                  {upload.hasFile && (
                    <Button variant="default" leftSection={<IconEye size={18} />} loading={viewing} disabled={busy} onClick={() => onView(upload)}>
                      View
                    </Button>
                  )}
                  <Button variant="default" leftSection={<IconRefresh size={18} />} disabled={busy} onClick={() => { setStatus(null); setReplacing(true); }}>
                    Replace
                  </Button>
                </div>
                {upload.hasFile && (
                  <Button variant="subtle" color="red" fullWidth leftSection={<IconTrash size={18} />} disabled={busy} onClick={confirmDelete}>
                    Delete this document
                  </Button>
                )}
              </>
            )}

            {showUploader && (
              <>
                {type.helpText && <p className="rm-small" style={{ color: 'var(--rides-ink)', fontSize: 15 }}>{type.helpText}</p>}
                {type.askExpiryDate && (
                  <ExpiryDateInput
                    label="Expiry date on the document"
                    description="We'll ask for a new copy after this date."
                    value={expiry}
                    onChange={(ymd) => setExpiry(ymd ? parseApiDate(ymd) : null)}
                    minDate={toYmd(tomorrow())}
                    required
                    disabled={busy}
                  />
                )}
                {type.isChildSafetyClearance && (
                  <Checkbox
                    checked={consent}
                    onChange={(e) => setConsent(e.currentTarget.checked)}
                    disabled={busy}
                    size="md"
                    styles={{ body: { alignItems: 'flex-start' }, label: { paddingInlineStart: 10 } }}
                    label={t('docs.clearance_consent')}
                  />
                )}
                <div className="rm-btnrow">
                  <Button
                    leftSection={<IconUpload size={18} />}
                    onClick={() => galleryRef.current?.click()}
                    disabled={!canPick}
                    variant={level === 'required' && !live ? 'filled' : 'default'}
                  >
                    {upload ? 'Upload a new one' : type.allowPdf ? 'Upload a photo or PDF' : 'Upload a photo'}
                  </Button>
                  {touch && (
                    <Button leftSection={<IconCamera size={18} />} onClick={() => cameraRef.current?.click()} disabled={!canPick} variant="default">
                      Take a photo
                    </Button>
                  )}
                </div>
                {replacing && (
                  <Button variant="subtle" fullWidth disabled={busy} onClick={() => setReplacing(false)}>
                    Cancel
                  </Button>
                )}
                {!canPick && !busy && (
                  <p className="rm-small">
                    {!expiryOk && !consentOk
                      ? 'First enter the expiry date and tick the box above.'
                      : !expiryOk
                        ? 'First enter the expiry date printed on the document.'
                        : 'First tick the box above.'}
                  </p>
                )}
                <p className="rm-small">{type.allowPdf ? `A clear photo or a PDF, up to ${maxMb} MB.` : `A clear photo, up to ${maxMb} MB.`}</p>
                <input ref={galleryRef} type="file" accept={accept} onChange={onPick} style={{ display: 'none' }} tabIndex={-1} aria-hidden="true" />
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onPick} style={{ display: 'none' }} tabIndex={-1} aria-hidden="true" />
              </>
            )}
          </>
        )}
      </div>

      <div role="status" aria-live="polite" style={{ padding: busy || status ? '0 12px 12px' : 0 }}>
        {busy && (
          <>
            <Progress value={actions.stage === 'deleting' ? 100 : actions.progress} animated striped size="sm" aria-label={DOCUMENT_STAGE_TEXT[actions.stage]} />
            <p className="rm-small" style={{ marginTop: 4 }}>{DOCUMENT_STAGE_TEXT[actions.stage]}</p>
          </>
        )}
        {status && !actions.error && !busy && <p className="rm-ok-text">{status}</p>}
      </div>

      {actions.error && (
        <div style={{ padding: '0 12px 12px' }}>
          <Callout tone="bad" live action={<Button variant="default" onClick={actions.clearError}>OK</Button>}>{actions.error}</Callout>
        </div>
      )}
    </div>
  );
}
